import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Clock, DollarSign, X, Upload, Send } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import '../styles/fonts.css';

const positions = [
  {
    id: 1,
    title: 'PSW/ Certified Restorative Assistant/Aide',
    location: 'On-site',
    type: 'Full-time',
    salary: '$50k - $70k',
    description: 'Requirement: PSW Diploma Graduate or Certified Restorative Assistant/Aide with experience in a healthcare setting. Responsibilities include assisting clients with daily activities, implementing restorative care plans, and collaborating with healthcare professionals to enhance client well-being.',
  },
  // {
  //   id: 2,
  //   title: 'Office Assistant',
  //   location: 'On-site',
  //   type: 'Part-time',
  //   salary: '$35k - $45k',
  //   description: 'Design and implement customized fitness programs for individual clients.',
  // },
];

// Toast
function showToast(message) {
  const toast = document.getElementById("toast");
  toast.textContent = message;
  toast.classList.add("show");

  setTimeout(() => toast.classList.remove("show"), 3000);
}

export default function CareersSection() {
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // if (!phone) return true; // Phone is optional
    // Only allow numbers, spaces, dashes, parentheses, and plus sign
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 7;
  };

  const handleApply = (position) => {
    setSelectedPosition(position);
    setShowModal(true);
  };

  const handleChange = (e) => {
    // setFormData({ ...formData, [e.target.name]: e.target.value });
       const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate email on change
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }

    // Validate phone on change
    if (name === 'phone') {
      if (value && !validatePhone(value)) {
        setPhoneError('Please enter a valid phone number (numbers only)');
      } else {
        setPhoneError('');
      }
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'resume') setResume(file);
    if (type === 'coverLetter') setCoverLetter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email and phone number before submission
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

     if (!validatePhone(formData.phone)) {
      setPhoneError('Please enter a valid phone number (numbers only)');
      return;
    }
    setIsSubmitting(true);

    const submitData = new FormData();
    submitData.append('position', selectedPosition.title);
    submitData.append('name', formData.name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('message', formData.message);
    if (resume) submitData.append('resume', resume);
    if (coverLetter) submitData.append('coverLetter', coverLetter);

    try {
      const response = await fetch('https://svr.restorativeconnections.ca/apply', {
        method: 'POST',
        body: submitData,
      });

      if (response.ok) {
        alert('Application submitted successfully!');
        setShowModal(false);
        setFormData({ name: '', email: '', phone: '', message: ''});
        setResume(null);
        setCoverLetter(null);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Application submitted! Thank you for applying.');
      setFormData({ name: '', email: '', phone: '', message: ''});
      setResume(null);
      setCoverLetter(null);
      setShowModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="careers" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-emerald-600 font-medium text-sm uppercase tracking-wider">Join Our Team</span>
          <h2 className="text-4xl md:text-5xl kaushan-script font-bold text-gray-900 mt-3 mb-6">
            Career <span className="text-emerald-600">Opportunities</span>
          </h2>
          <p className="text-gray-600 text-lg fuzzy-bubbles  max-w-5xl mx-auto">
            Be part of a team dedicated to transforming lives through Restorative therapy programs and services.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {positions.map((position, index) => (
            <motion.div
              key={position.id}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Briefcase className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{position.title}</h3>
              <p className="text-gray-600 mb-4">{position.description}</p>
              
              <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {position.location}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {position.type}
                </span>
                {/* <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" /> {position.salary}
                </span> */}
              </div>
              
              <Button
                onClick={() => handleApply(position)}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl"
              >
                Apply Now
              </Button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Application Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Apply for {selectedPosition?.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{selectedPosition?.location} • {selectedPosition?.type}</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="e-mail@example.com"
                       className={`h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 ${emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                    />
                    {emailError && (
                      <p className="mt-1 text-sm text-red-500">{emailError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (555) 000-0000"
                      className={`h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 ${phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                    />
                     {phoneError && (
                      <p className="mt-1 text-sm text-red-500">{phoneError}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resume *</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'resume')}
                      required
                      className="hidden"
                      id="resume"
                    />
                    <label
                      htmlFor="resume"
                      className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-500 cursor-pointer transition-colors"
                    >
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">{resume ? resume.name : 'Upload Resume (PDF, DOC)'}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'coverLetter')}
                      className="hidden"
                      id="coverLetter"
                    />
                    <label
                      htmlFor="coverLetter"
                      className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-500 cursor-pointer transition-colors"
                    >
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">{coverLetter ? coverLetter.name : 'Upload Cover Letter (Optional)'}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us why you're interested in this position..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl h-12"
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="px-6 rounded-xl h-12"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
